import { Trans } from "@lingui/macro";
import {
  UiFormControlLabel,
  UiRadio,
  UiRadioGroupUncustomized,
  UiTextFieldV2,
  UiTypography,
} from "@smp/ui";
import { getIn, useFormikContext } from "formik";
import { FC, ReactNode, RefObject, useMemo, useState } from "react";
import { useMount, useUpdateEffect } from "react-use";

import { ICreateFormValues } from "../../create_form.types";
import classes from "./radio_group.module.css";

export interface IRadioGroupProps {
  name: string;
  options: string[];
  title: ReactNode;
  placeholder: string;
  buttonsRef?: RefObject<HTMLDivElement>;
  disabled?: boolean;
}

export const RadioGroup: FC<IRadioGroupProps> = ({
  options,
  name,
  title,
  placeholder,
  buttonsRef,
  disabled,
}) => {
  const form = useFormikContext<ICreateFormValues>();

  const [selectedOption, setSelectedOption] = useState("");
  const [customFieldValue, setCustomFieldValue] = useState("");

  const definedOptions = useMemo(
    () =>
      options.map((option, idx) => ({
        value: String(idx),
        label: option,
      })),
    [options],
  );

  const errorMessage = useMemo(() => {
    if (selectedOption !== "custom") {
      return undefined;
    }

    return form.errors[name] as string | undefined;
  }, [form.errors, name, selectedOption]);

  useUpdateEffect(() => {
    const newValue =
      selectedOption === "custom"
        ? customFieldValue
        : definedOptions.find((option) => option.value === selectedOption)
            ?.label;

    form.setFieldValue(name, newValue);
  }, [selectedOption, customFieldValue]);

  useUpdateEffect(() => {
    if (selectedOption && buttonsRef?.current) {
      buttonsRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedOption]);

  useMount(() => {
    const curValue = getIn(form.values, name);

    if (!curValue) {
      return;
    }

    const currentOptionsIndex = options.findIndex(
      (option) => option === curValue,
    );

    if (currentOptionsIndex < 0) {
      setCustomFieldValue(curValue);
      setSelectedOption("custom");
    } else {
      setSelectedOption(String(currentOptionsIndex));
    }
  });

  return (
    <div className={classes.form}>
      <UiTypography variant="h4">{title}</UiTypography>

      <UiTypography variant="h5">
        <Trans>Select one of the proposed options or offer your</Trans>
      </UiTypography>

      <UiRadioGroupUncustomized
        name={name}
        className={classes.group}
        value={selectedOption}
        onChange={(_e, value) => setSelectedOption(value)}
      >
        {definedOptions.map(({ value, label }) => (
          <UiFormControlLabel
            key={value}
            className={classes.label}
            control={
              <UiRadio
                color="primary"
                className={classes.label__control}
                disabled={disabled}
              />
            }
            label={label}
            value={value}
            componentsProps={{
              typography: {
                component: "div",
                variant: "body2",
                className: classes.label__text,
              },
            }}
          />
        ))}

        <UiFormControlLabel
          className={classes.label}
          control={
            <UiRadio
              color="primary"
              className={classes.label__control}
              disabled={disabled}
            />
          }
          label={
            <>
              <div className={classes.customLabel__text}>
                <Trans>My description</Trans>
              </div>

              <div>
                <UiTextFieldV2
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={2}
                  placeholder={placeholder}
                  onFocus={() => setSelectedOption("custom")}
                  inputProps={{ tabIndex: -1 }}
                  value={customFieldValue}
                  onChange={(e) => setCustomFieldValue(e.target.value)}
                  error={!!errorMessage}
                  errorMessage={errorMessage}
                />
              </div>
            </>
          }
          value="custom"
          componentsProps={{
            typography: {
              component: "div",
              variant: "body2",
              className: classes.customLabel,
            },
          }}
        />
      </UiRadioGroupUncustomized>
    </div>
  );
};
